/**
 * The base colors for the Vero Software site, keyed by light/dark mode.
 * Blue (from the Vero logo) is the dominant brand color; the logo's red and
 * green are reserved for small accents (success/error semantics) rather than
 * primary UI chrome.
 */

const shared = {
  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  success: {
    main: "#1c9738",
    focus: "#22ab41",
  },

  warning: {
    main: "#fb8c00",
    focus: "#fc9d26",
  },

  error: {
    main: "#cd0000",
    focus: "#e0231c",
  },

  socialMediaColors: {
    facebook: { main: "#3b5998", dark: "#344e86" },
    twitter: { main: "#55acee", dark: "#3ea1ec" },
    instagram: { main: "#125688", dark: "#0e456d" },
    linkedin: { main: "#0077b5", dark: "#00669c" },
    pinterest: { main: "#cc2127", dark: "#b21d22" },
    youtube: { main: "#e52d27", dark: "#d41f1a" },
    vimeo: { main: "#1ab7ea", dark: "#13a3d2" },
    slack: { main: "#3aaf85", dark: "#329874" },
    dribbble: { main: "#ea4c89", dark: "#e73177" },
    github: { main: "#24292e", dark: "#171a1d" },
    reddit: { main: "#ff4500", dark: "#e03d00" },
    tumblr: { main: "#35465c", dark: "#2a3749" },
  },

  badgeColors: {
    primary: { background: "#cfe2f7", text: "#0f4c8c" },
    secondary: { background: "#d7d9e1", text: "#6c757d" },
    info: { background: "#aecef7", text: "#0f4c8c" },
    success: { background: "#bce2be", text: "#1c9738" },
    warning: { background: "#ffd59f", text: "#c87000" },
    error: { background: "#fcd3d0", text: "#cd0000" },
    light: { background: "#ffffff", text: "#c7d3de" },
    dark: { background: "#8097bf", text: "#1e2e4a" },
  },

  coloredShadows: {
    primary: "#0f4c8c",
    secondary: "#110e0e",
    info: "#1d66b1",
    success: "#1c9738",
    warning: "#ff9900",
    error: "#cd0000",
    light: "#adb5bd",
    dark: "#404040",
  },

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

const light = {
  ...shared,

  background: {
    default: "#f3f6f9",
  },

  text: {
    main: "#55697a",
    focus: "#55697a",
  },

  primary: {
    main: "#0f4c8c",
    focus: "#0d3f73",
  },

  secondary: {
    main: "#64748b",
    focus: "#75839a",
  },

  info: {
    main: "#1d66b1",
    focus: "#175293",
  },

  light: {
    main: "#f0f2f5",
    focus: "#f0f2f5",
  },

  dark: {
    main: "#122436",
    focus: "#0c1a28",
  },

  surface: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  gradients: {
    primary: { main: "#14508c", state: "#0a2e52" },
    secondary: { main: "#8a95a6", state: "#64748b" },
    info: { main: "#4a8fd6", state: "#1d66b1" },
    success: { main: "#4caf50", state: "#1c9738" },
    warning: { main: "#ffa726", state: "#fb8c00" },
    error: { main: "#e0231c", state: "#cd0000" },
    light: { main: "#ebeff4", state: "#ced4da" },
    dark: { main: "#1f2937", state: "#0f172a" },
  },

  inputBorderColor: "#d2d6da",
};

const dark = {
  ...shared,

  background: {
    default: "#0a121b",
  },

  text: {
    main: "#a4b6c4",
    focus: "#c2d1dc",
  },

  primary: {
    main: "#4a8fd6",
    focus: "#6ba5e0",
  },

  secondary: {
    main: "#8a95a6",
    focus: "#a3adba",
  },

  info: {
    main: "#4a8fd6",
    focus: "#6ba5e0",
  },

  light: {
    main: "#1c2733",
    focus: "#1c2733",
  },

  dark: {
    main: "#eef2f6",
    focus: "#ffffff",
  },

  surface: {
    main: "#131c27",
    focus: "#131c27",
  },

  grey: {
    100: "#212529",
    200: "#2b3138",
    300: "#343a40",
    400: "#495057",
    500: "#6c757d",
    600: "#adb5bd",
    700: "#ced4da",
    800: "#dee2e6",
    900: "#f0f2f5",
  },

  gradients: {
    primary: { main: "#4a8fd6", state: "#14508c" },
    secondary: { main: "#8a95a6", state: "#64748b" },
    info: { main: "#4a8fd6", state: "#175293" },
    success: { main: "#4caf50", state: "#1c9738" },
    warning: { main: "#ffa726", state: "#fb8c00" },
    error: { main: "#e0231c", state: "#cd0000" },
    light: { main: "#2b3138", state: "#343a40" },
    dark: { main: "#1f2937", state: "#0f172a" },
  },

  inputBorderColor: "#33414d",
};

export default function getColors(mode) {
  return mode === "dark" ? dark : light;
}
